import { useEffect, useRef } from "react";
import { Instagram, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { INSTAGRAM_REELS, SOCIAL_LINKS } from "../../data";

// Instagram's official embed script. Loaded once on mount; re-run
// window.instgrm.Embeds.process() whenever new blockquotes appear.
const IG_SCRIPT_SRC = "https://www.instagram.com/embed.js";

function ensureInstagramScript(onReady) {
  if (typeof window === "undefined") return;
  if (window.instgrm && window.instgrm.Embeds) {
    onReady();
    return;
  }
  const existing = document.querySelector(`script[src="${IG_SCRIPT_SRC}"]`);
  if (existing) {
    existing.addEventListener("load", onReady, { once: true });
    return;
  }
  const s = document.createElement("script");
  s.src = IG_SCRIPT_SRC;
  s.async = true;
  s.defer = true;
  s.onload = onReady;
  document.body.appendChild(s);
}

function ReelEmbed({ url }) {
  return (
    <blockquote
      className="instagram-media"
      data-instgrm-permalink={url}
      data-instgrm-version="14"
      data-instgrm-captioned
      style={{
        background: "#111",
        border: 0,
        borderRadius: "12px",
        margin: 0,
        maxWidth: "100%",
        minWidth: 0,
        width: "100%",
        padding: 0,
      }}
    >
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        style={{ color: "#fff" }}
      >
        View reel on Instagram
      </a>
    </blockquote>
  );
}

export default function Reels() {
  const reels = (INSTAGRAM_REELS || []).filter(Boolean);
  const ref = useRef(null);

  useEffect(() => {
    if (reels.length === 0) return;
    let cancelled = false;
    ensureInstagramScript(() => {
      if (cancelled) return;
      // Instagram processes any unprocessed blockquotes in the DOM.
      try {
        window.instgrm?.Embeds?.process();
      } catch (_e) {
        /* noop — Instagram will retry on next mount */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [reels.length]);

  if (reels.length === 0) return null;

  return (
    <section
      id="reels"
      className="relative bg-[#0A0A0A] py-24 md:py-36 px-6 lg:px-10 overflow-hidden"
      data-testid="reels-section"
    >
      <div className="pointer-events-none absolute -top-24 left-0 h-[420px] w-[420px] rounded-full bg-teal/[0.08] blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-32 right-0 h-[420px] w-[420px] rounded-full bg-hotpink/[0.08] blur-[140px]" />

      <div className="relative mx-auto max-w-[1400px]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <Reveal>
              <p className="font-body text-xs md:text-sm uppercase tracking-[0.3em] text-teal mb-6">
                / Live from Instagram
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-700 uppercase leading-[0.9] tracking-tighter text-white text-5xl md:text-6xl lg:text-7xl">
                On the mats
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl font-body text-base md:text-lg text-neutral-400 leading-relaxed">
                Fresh training clips, technique breakdowns, and moments from
                the room —{" "}
                <span className="text-white">
                  {SOCIAL_LINKS.instagramHandle}
                </span>
                .
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <a
              href={SOCIAL_LINKS.instagramUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="reels-follow-btn"
              className="inline-flex items-center gap-2 font-display uppercase tracking-wide text-sm px-6 py-3 border border-white/15 text-white bg-white/5 hover:bg-hotpink hover:border-hotpink transition-colors duration-300 rounded-sm whitespace-nowrap"
            >
              <Instagram size={16} /> Follow the room
              <ArrowUpRight size={14} />
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.25}>
          <div
            ref={ref}
            data-testid="reels-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {reels.map((url) => (
              <div
                key={url}
                data-testid="reel-card"
                className="rounded-xl border border-white/10 bg-[#111] overflow-hidden"
              >
                <ReelEmbed url={url} />
              </div>
            ))}
          </div>
        </Reveal>

        <p className="mt-8 font-body text-[10px] uppercase tracking-[0.25em] text-neutral-600">
          Reels are loaded live from Instagram. If a clip does not appear you
          may be using a content blocker.
        </p>
      </div>
    </section>
  );
}
